import { APIError } from "@bvhi/backend-api/api-error";
import { setBaseUrl } from "@bvhi/backend-api/custom-fetch-instance";
import { Box, CssBaseline } from "@bvhi/ui-base";
import { ThemeProvider } from "@bvhi/ui-theme";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import dayjs from "dayjs";
import "dayjs/locale/de";
import { SnackbarProvider } from "notistack";
import { Suspense } from "react";
import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import { BounceLoader } from "react-spinners";
import { App } from "./app";
import { ErrorCard } from "./components/error-card";
import { NotistackAlert } from "./components/notistack-alert";
import { getAppConfig } from "./config/config";
import { AuthContextProvider } from "./context/auth-context-provider";
import { hasFeatureEnabled } from "./utils/env-helper.ts";
import "./utils/yup-custom-validations";

// Uncomment the following lines to change i18next language to cimode
// import { setAppConfig } from "./config/config";
// setAppConfig({ language: "cimode" });

// Uncomment the following line to active mock service worker
// import { setAppConfig } from "./config/config";
// axiosInstance.defaults.baseURL = "";
// setAppConfig({ env: { gatewayUrl: "" } });
// setAppConfig({ enableMsw: true });

dayjs.locale("de");

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if ((error as APIError).status === 401 && (window.location.href.includes("/app") || window.location.href.includes("/profile"))) {
        window.location.href = "/";
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
});

setBaseUrl(getAppConfig().env.gatewayUrl);

async function enableMsw() {
  if (!hasFeatureEnabled("USE_MSW")) return;
  const { worker } = await import("../mocks/browser");
  return worker.start({ onUnhandledRequest: "bypass" });
}

void enableMsw().then(() => {
  createRoot(document.getElementById("root")!).render(
    <ErrorBoundary
      fallback={
        <Box sx={{ position: "absolute", left: 0, right: 0, top: 0, bottom: 0, background: "#e5ecf7" }}>
          <ErrorCard
            sx={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
            }}
          />
        </Box>
      }
    >
      <Suspense
        fallback={
          <BounceLoader color="#2e66b2" cssOverride={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }} />
        }
      >
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <SnackbarProvider
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              autoHideDuration={4000}
              Components={{
                success: NotistackAlert,
                info: NotistackAlert,
                error: NotistackAlert,
                warning: NotistackAlert,
              }}
            >
              <CssBaseline />
              <AuthContextProvider>
                <App />
              </AuthContextProvider>
            </SnackbarProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </Suspense>
    </ErrorBoundary>
  );
});
